import React, { useState, useEffect } from 'react';
import './AdminFeedback.css';

const AdminFeedback = () => {
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'pending',
    type: 'all',
    priority: 'all',
    search: ''
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [respondingTo, setRespondingTo] = useState(null);
  const [response, setResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchFeedback();
    fetchStats();
  }, [filters, page]);

  const fetchFeedback = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams({
        ...filters,
        page,
        limit: 10
      });

      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(
        `${apiUrl}/api/feedback/admin/all?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();
      if (response.ok) {
        setFeedback(data.feedback);
        setTotalPages(data.totalPages);
      }
    } catch (err) {
      console.error('Error fetching feedback:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(
        `${apiUrl}/api/feedback/admin/stats`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();
      if (response.ok) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleRespond = async (feedbackId) => {
    if (!response.trim()) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const res = await fetch(
        `${apiUrl}/api/feedback/admin/${feedbackId}/respond`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ response })
        }
      );

      if (res.ok) {
        setRespondingTo(null);
        setResponse('');
        fetchFeedback();
        fetchStats();
      }
    } catch (err) {
      console.error('Error responding:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (feedbackId, newStatus) => {
    try {
      const token = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      await fetch(
        `${apiUrl}/api/feedback/admin/${feedbackId}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ status: newStatus })
        }
      );
      fetchFeedback();
      fetchStats();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handlePriorityChange = async (feedbackId, newPriority) => {
    try {
      const token = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      await fetch(
        `${apiUrl}/api/feedback/admin/${feedbackId}/priority`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ priority: newPriority })
        }
      );
      fetchFeedback();
      fetchStats();
    } catch (err) {
      console.error('Error updating priority:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading">Loading feedback...</div>;
  }

  return (
    <div className="admin-feedback-container">
      <div className="admin-header">
        <h1>Feedback Management</h1>
      </div>

      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total</div>
          </div>
          <div className="stat-card pending">
            <div className="stat-value">{stats.pending}</div>
            <div className="stat-label">Pending</div>
          </div>
          <div className="stat-card in-progress">
            <div className="stat-value">{stats.in_progress}</div>
            <div className="stat-label">In Progress</div>
          </div>
          <div className="stat-card resolved">
            <div className="stat-value">{stats.resolved}</div>
            <div className="stat-label">Resolved</div>
          </div>
          <div className="stat-card high-priority">
            <div className="stat-value">{stats.high_priority}</div>
            <div className="stat-label">High Priority</div>
          </div>
        </div>
      )}

      <div className="filters-section">
        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filters.status}
            onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Type:</label>
          <select
            value={filters.type}
            onChange={(e) => { setFilters({ ...filters, type: e.target.value }); setPage(1); }}
          >
            <option value="all">All</option>
            <option value="question">Question</option>
            <option value="feature_request">Feature Request</option>
            <option value="bug_report">Bug Report</option>
            <option value="country_request">Country Request</option>
            <option value="general">General</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Priority:</label>
          <select
            value={filters.priority}
            onChange={(e) => { setFilters({ ...filters, priority: e.target.value }); setPage(1); }}
          >
            <option value="all">All</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>

        <div className="filter-group search-group">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search subject or message..."
            value={filters.search}
            onChange={(e) => { setFilters({ ...filters, search: e.target.value }); setPage(1); }}
          />
        </div>
      </div>

      <div className="feedback-list">
        {feedback.map(item => (
          <div key={item.feedback_id} className={`feedback-card priority-${item.priority}`}>
            <div className="feedback-header-row">
              <div className="feedback-info">
                <span className="feedback-type">{item.type.replace('_', ' ')}</span>
                <span className="feedback-submitter">
                  by {item.submitter_first_name || 'User'} (Trust: {item.submitter_trust_score})
                </span>
                <span className="feedback-date">{formatDate(item.created_at)}</span>
              </div>
              <div className="feedback-controls">
                <select
                  value={item.priority}
                  onChange={(e) => handlePriorityChange(item.feedback_id, e.target.value)}
                  className={`priority-select priority-${item.priority}`}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
                <select
                  value={item.status}
                  onChange={(e) => handleStatusChange(item.feedback_id, e.target.value)}
                  className="status-select"
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>

            <h3 className="feedback-subject">{item.subject}</h3>
            <p className="feedback-message">{item.message}</p>

            {item.admin_response ? (
              <div className="existing-response">
                <strong>Your Response:</strong>
                <p>{item.admin_response}</p>
                <small>Responded by {item.responder_first_name} on {formatDate(item.responded_at)}</small>
              </div>
            ) : (
              <div className="response-section">
                {respondingTo === item.feedback_id ? (
                  <div className="response-form">
                    <textarea
                      value={response}
                      onChange={(e) => setResponse(e.target.value)}
                      placeholder="Type your response..."
                      rows={4}
                      maxLength={1000}
                    />
                    <div className="response-actions">
                      <button onClick={() => { setRespondingTo(null); setResponse(''); }}>
                        Cancel
                      </button>
                      <button
                        onClick={() => handleRespond(item.feedback_id)}
                        disabled={submitting || !response.trim()}
                        className="btn-primary"
                      >
                        {submitting ? 'Sending...' : 'Send Response'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setRespondingTo(item.feedback_id)}
                    className="btn-respond"
                  >
                    Respond
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminFeedback;
